import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https://www.gov.br/ds/components/tab?tab=designer).
 *
 * @slot default - Slot com os componentes br-tab-item
 *
 * @part container - Container principal do componente
 * @part nav - Elemento de navegação das tabs
 *
 * @example
 * ```html
 * <br-tab>
 *   <br-tab-item tab-item-title="Equipe">Conteúdo da equipe</br-tab-item>
 *   <br-tab-item tab-item-title="Projetos">Conteúdo de projetos</br-tab-item>
 * </br-tab>
 * ```
 */
@Component({
  tag: 'br-tab',
  styleUrl: 'tab.scss',
  shadow: true,
})
export class Tab {
  /**
   * Referência ao elemento host do componente.
   */
  @Element() el: HTMLBrTabElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * A propriedade 'density' define a densidade do componente.
   * Os valores possíveis são 'small', 'medium' e 'large'.
   */
  @Prop({ reflect: true }) readonly density: 'small' | 'medium' | 'large' = 'medium'

  /**
   * Define se o Tab usará um esquema de cores escuro.
   */
  @Prop({ reflect: true }) readonly colorMode: 'dark'

  /**
   * Label de acessibilidade para a navegação das tabs
   */
  @Prop({ reflect: true }) readonly label: string = 'Abas de Navegação'

  /**
   * Define o alinhamento horizontal dos itens da tab na barra de navegação.
   * - 'start': Alinha à esquerda
   * - 'center': Centraliza
   * - 'end': Alinha à direita
   */
  @Prop({ reflect: true }) readonly alignItemsTab: 'start' | 'center' | 'end' = 'start'

  /**
   * Lista dos tab items registrados
   */
  @State() private tabItems: HTMLBrTabItemElement[] = []

  /**
   * ID do tab atualmente ativo
   */
  @State() private activeTabId: string

  /**
   * Evento disparado quando um tab é ativado.
   */
  @Event() brTabChange: EventEmitter<{ tabId: string; tabIndex: number }>

  private slotElement: HTMLSlotElement
  private mutationObserver: MutationObserver

  /**
   * Observa mudanças no tab ativo para atualizar os estados
   */
  @Watch('activeTabId')
  activeTabChanged(newId: string) {
    this.updateTabStates()
    const activeIndex = this.tabItems.findIndex((item) => item.tabItemId === newId)
    this.brTabChange.emit({ tabId: newId, tabIndex: activeIndex })
  }

  /**
   * Observa mudanças no color mode para propagar aos filhos
   */
  @Watch('colorMode')
  colorModeChanged(newValue: string) {
    this.tabItems.forEach((item) => {
      if (newValue !== undefined && newValue !== null && typeof newValue === 'string') {
        item.setAttribute('color-mode', newValue)
      } else {
        item.removeAttribute('color-mode')
      }
    })
  }

  /**
   * Escuta evento de mudança de propriedades dos tab-items filhos.
   * Este listener garante que o componente pai seja re-renderizado quando
   * propriedades como tabItemTitle, icon, counter ou onlyIcon mudam dinamicamente.
   */
  @Listen('brTabItemPropsChange')
  handleTabItemPropsChange() {
    // Força re-renderização para atualizar a navegação
    this.tabItems = [...this.tabItems]
  }

  private registerTabItems = () => {
    if (this.slotElement === undefined || this.slotElement === null) return

    const assignedElements = this.slotElement.assignedElements() as HTMLBrTabItemElement[]
    this.tabItems = assignedElements.filter((el) => el.tagName?.toLowerCase() === 'br-tab-item')

    // Garante que cada tab item tenha um ID único
    this.tabItems.forEach((item) => {
      if (
        item.tabItemId === undefined ||
        item.tabItemId === null ||
        (typeof item.tabItemId === 'string' && item.tabItemId === '') ||
        item.hasAttribute('tab-item-id') === false
      ) {
        const id = Helpers.generateUniqueId()
        item.tabItemId = id
        item.setAttribute('tab-item-id', id)
      }

      // Propaga color mode
      if (this.colorMode !== undefined && this.colorMode !== null && typeof this.colorMode === 'string') {
        item.setAttribute('color-mode', this.colorMode)
      }
    })

    // Define o primeiro tab como ativo se nenhum estiver ativo
    if (
      (this.activeTabId === undefined ||
        this.activeTabId === null ||
        (typeof this.activeTabId === 'string' && this.activeTabId === '')) &&
      this.tabItems.length > 0
    ) {
      const activeItem = this.tabItems.find((item) => item.isActive === true)
      this.activeTabId = activeItem?.tabItemId || this.tabItems[0].tabItemId
    }

    this.updateTabStates()
  }

  /**
   * Atualiza o estado ativo de todos os tab items
   * Comunicação direta similar ao Vue
   */
  private updateTabStates() {
    this.tabItems.forEach((item) => {
      const isActive = item.tabItemId === this.activeTabId
      item.isActive = isActive

      // Atualiza atributos para compatibilidade
      if (isActive) {
        item.setAttribute('is-active', 'true')
        item.setAttribute('aria-selected', 'true')
      } else {
        item.removeAttribute('is-active')
        item.removeAttribute('aria-selected')
      }
    })
  }

  /**
   * Manipula o clique em um botão de tab
   */
  private handleTabClick(tabId: string) {
    if (this.activeTabId !== tabId) {
      this.activeTabId = tabId
    }
  }

  /**
   * Manipula navegação por teclado (acessibilidade)
   */
  private handleKeyNavigation(currentIndex: number, event: KeyboardEvent) {
    let newIndex = currentIndex

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        newIndex = (currentIndex + 1) % this.tabItems.length
        break
      case 'ArrowLeft':
        event.preventDefault()
        newIndex = (currentIndex - 1 + this.tabItems.length) % this.tabItems.length
        break
      case 'Home':
        event.preventDefault()
        newIndex = 0
        break
      case 'End':
        event.preventDefault()
        newIndex = this.tabItems.length - 1
        break
      case 'ArrowDown':
        event.preventDefault()
        // Foca no conteúdo do painel ativo
        const activePanel = this.tabItems[currentIndex]
        if (activePanel !== undefined && activePanel !== null && typeof activePanel === 'object') {
          const focusableElements = activePanel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
          if (focusableElements.length > 0) {
            ;(focusableElements[0] as HTMLElement).focus()
          }
        }
        return
      default:
        return
    }

    // Ativa e foca o novo tab
    if (newIndex !== currentIndex) {
      this.activeTabId = this.tabItems[newIndex].tabItemId
      // O foco será definido no próximo render através do tabindex
      setTimeout(() => {
        const buttons = this.el.shadowRoot.querySelectorAll('.tab-item button')
        ;(buttons[newIndex] as HTMLElement)?.focus()
      }, 0)
    }
  }

  /**
   * Retorna o mapeamento de classes CSS do componente
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-tab': true,
      small: this.density === 'small',
      medium: this.density === 'medium',
      large: this.density === 'large',
      'dark-mode': this.colorMode === 'dark',
    }
  }

  /**
   * Retorna o mapeamento de classes CSS para cada item de navegação
   */
  private getTabItemCssClassMap(isActive: boolean): CssClassMap {
    return {
      'tab-item': true,
      active: isActive,
      'dark-mode': this.colorMode === 'dark',
    }
  }

  /**
   * Retorna o alinhamento em formato flexbox
   */
  private getAlignItemsStyle(): string {
    const alignMap = {
      start: 'flex-start',
      center: 'center',
      end: 'flex-end',
    }
    return alignMap[this.alignItemsTab] || 'flex-start'
  }

  componentDidLoad() {
    // Observa mudanças no slot
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => {
        this.registerTabItems()
      })

      this.mutationObserver.observe(this.el, {
        childList: true,
        subtree: false,
      })
    }

    this.registerTabItems()
  }

  disconnectedCallback() {
    if (
      this.mutationObserver !== undefined &&
      this.mutationObserver !== null &&
      typeof this.mutationObserver === 'object'
    ) {
      this.mutationObserver.disconnect()
    }
  }

  render() {
    const hasCounter = this.tabItems.some((item) => item.counter)

    return (
      <Host>
        <div class={this.getCssClassMap()} id={this.customId} part="container">
          {/* Navegação das tabs */}
          <nav class="tab-nav" data-counter={hasCounter ? 'true' : 'false'} aria-label={this.label} part="nav">
            <ul role="tablist" style={{ justifyContent: this.getAlignItemsStyle() }}>
              {this.tabItems.map((item, index) => {
                const isActive = item.tabItemId === this.activeTabId
                const showOnlyIcon = item.onlyIcon || item.hasAttribute('only-icon')

                return (
                  <li key={item.tabItemId} class={this.getTabItemCssClassMap(isActive)} role="presentation">
                    <button
                      type="button"
                      id={`tab-button-${item.tabItemId}`}
                      role="tab"
                      aria-selected={isActive ? 'true' : 'false'}
                      aria-controls={`tab-panel-${item.tabItemId}`}
                      tabindex={isActive ? 0 : -1}
                      onClick={() => this.handleTabClick(item.tabItemId)}
                      onKeyDown={(e) => this.handleKeyNavigation(index, e)}
                    >
                      <span class="name">
                        {item.icon && (
                          <span class="icon">
                            <br-icon icon-name={item.icon}></br-icon>
                          </span>
                        )}
                        {!showOnlyIcon && <span>{item.tabItemTitle}</span>}
                      </span>
                    </button>
                    {item.counter && <span class="results">({item.counter})</span>}
                  </li>
                )
              })}
            </ul>
          </nav>

          <div class="tab-content">
            <slot
              ref={(el) => {
                this.slotElement = el as HTMLSlotElement
              }}
              onSlotchange={this.registerTabItems}
            ></slot>
          </div>
        </div>
      </Host>
    )
  }
}
